// src/Views/PredictionHistory/PredictionHistory.js
// 📊 PREDICTION HISTORY - Track all V10 & HRHR predictions + results
//
// Features:
// - View ALL past V10 and HRHR predictions (no 5-item cap)
// - Filter by engine (All / V10 / HRHR)
// - Mark predictions as Won ✅ / Lost ❌ / Pending ⏳
// - Performance stats (win rate, streak, ROI tracking)
// - Expandable cards with full analysis
// - Paginated loading (20 at a time)

import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Icon } from '@iconify/react';
import './PredictionHistory.css';

import {
    collection,
    query,
    orderBy,
    limit,
    startAfter,
    getDocs,
    doc,
    updateDoc,
    where,
    serverTimestamp
} from 'firebase/firestore';

const PAGE_SIZE = 20;

const PredictionHistory = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Data
    const [predictions, setPredictions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    // Filters
    const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'v10' | 'hrhr'

    // UI
    const [expandedId, setExpandedId] = useState(null);
    const [updatingResult, setUpdatingResult] = useState(null); // doc id being updated

    // Stats (computed from loaded data)
    const [stats, setStats] = useState({
        total: 0,
        won: 0,
        lost: 0,
        pending: 0,
        winRate: 0
    });

    // =======================================================
    // FETCH PREDICTIONS
    // =======================================================
    const fetchPredictions = useCallback(async (isLoadMore = false) => {
        if (!user?.uid) return;

        if (isLoadMore) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const historyRef = collection(db, 'users', user.uid, 'prediction_history');

            let q;
            if (isLoadMore && lastDoc) {
                q = query(
                    historyRef,
                    orderBy('createdAt', 'desc'),
                    startAfter(lastDoc),
                    limit(PAGE_SIZE)
                );
            } else {
                q = query(
                    historyRef,
                    orderBy('createdAt', 'desc'),
                    limit(PAGE_SIZE)
                );
            }

            const snapshot = await getDocs(q);

            const items = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
                createdAt: docSnap.data().createdAt?.toDate() || new Date(),
                resultUpdatedAt: docSnap.data().resultUpdatedAt?.toDate() || null
            }));

            if (isLoadMore) {
                setPredictions(prev => [...prev, ...items]);
            } else {
                setPredictions(items);
            }

            // Set pagination cursor
            if (snapshot.docs.length > 0) {
                setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
            }
            setHasMore(snapshot.docs.length === PAGE_SIZE);

        } catch (err) {
            console.error('[PredictionHistory] Failed to fetch:', err);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [user?.uid, lastDoc]);

    // Initial load
    useEffect(() => {
        if (user?.uid) {
            fetchPredictions(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid]);

    // =======================================================
    // COMPUTE STATS (from filtered predictions)
    // =======================================================
    useEffect(() => {
        const filtered = getFilteredPredictions();
        const won = filtered.filter(p => p.result === 'won').length;
        const lost = filtered.filter(p => p.result === 'lost').length;
        const pending = filtered.filter(p => !p.result || p.result === 'pending').length;
        const total = filtered.length;
        const decided = won + lost;
        const winRate = decided > 0 ? Math.round((won / decided) * 100) : 0;

        setStats({ total, won, lost, pending, winRate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [predictions, activeFilter]);

    // =======================================================
    // FILTER PREDICTIONS
    // =======================================================
    const getFilteredPredictions = () => {
        if (activeFilter === 'all') {
            // Show only V10 and HRHR (exclude v7_5 and basketball)
            return predictions.filter(p => p.agent === 'v10' || p.agent === 'hrhr');
        }
        return predictions.filter(p => p.agent === activeFilter);
    };

    // =======================================================
    // UPDATE RESULT (Won / Lost / Pending)
    // =======================================================
    const updateResult = async (predictionId, newResult) => {
        if (!user?.uid || !predictionId) return;
        setUpdatingResult(predictionId);

        try {
            const docRef = doc(db, 'users', user.uid, 'prediction_history', predictionId);
            await updateDoc(docRef, {
                result: newResult,
                resultUpdatedAt: serverTimestamp()
            });

            // Update local state
            setPredictions(prev =>
                prev.map(p =>
                    p.id === predictionId
                        ? { ...p, result: newResult, resultUpdatedAt: new Date() }
                        : p
                )
            );
        } catch (err) {
            console.error('[PredictionHistory] Failed to update result:', err);
        } finally {
            setUpdatingResult(null);
        }
    };

    // =======================================================
    // HELPERS
    // =======================================================
    const formatDate = (date) => {
        if (!date) return '';
        const now = new Date();
        const diffMs = now - date;
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffHours < 1) return 'Just now';
        if (diffHours < 24) return `${diffHours}h ago`;

        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    const getEngineBadge = (agent) => {
        switch (agent) {
            case 'v10':
                return { label: 'V10.0', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)' };
            case 'hrhr':
                return { label: '🎯 HRHR', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' };
            default:
                return { label: agent?.toUpperCase() || 'Unknown', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.15)' };
        }
    };

    const getResultInfo = (result) => {
        switch (result) {
            case 'won':
                return { label: 'Won', icon: '✅', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)' };
            case 'lost':
                return { label: 'Lost', icon: '❌', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' };
            default:
                return { label: 'Pending', icon: '⏳', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' };
        }
    };

    // Extract a short summary from the question (first match or first line)
    const getQuestionPreview = (question) => {
        if (!question) return 'No matches listed';
        const lines = question.trim().split('\n').filter(l => l.trim());
        if (lines.length <= 2) return question.trim();
        return `${lines[0].trim()} (+${lines.length - 1} more)`;
    };

    const filteredPredictions = getFilteredPredictions();

    // =======================================================
    // AUTH GATE
    // =======================================================
    if (!user) {
        return (
            <section className="predhistory">
                <div className="predhistory__container">
                    <div className="predhistory__auth-gate">
                        <Icon icon="solar:lock-keyhole-bold" className="predhistory__auth-icon" />
                        <h2>Sign in to view your prediction history</h2>
                        <p>Track your V10 and HRHR prediction performance over time.</p>
                        <button
                            className="predhistory__btn predhistory__btn--primary"
                            onClick={() => navigate('/signin')}
                        >
                            Sign In
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    // =======================================================
    // RENDER
    // =======================================================
    return (
        <section className="predhistory">
            <div className="predhistory__container">

                {/* HEADER */}
                <div className="predhistory__header">
                    <button
                        className="predhistory__back-btn"
                        onClick={() => navigate('/ai-assistant')}
                    >
                        <Icon icon="solar:arrow-left-linear" />
                        Back to AiPredict
                    </button>

                    <div className="predhistory__title-row">
                        <div>
                            <h1 className="predhistory__title">
                                <Icon icon="solar:chart-2-bold-duotone" className="predhistory__title-icon" />
                                Prediction History
                            </h1>
                            <p className="predhistory__subtitle">
                                Track your V10 &amp; HRHR prediction performance
                            </p>
                        </div>
                    </div>
                </div>

                {/* STATS CARDS */}
                <div className="predhistory__stats">
                    <div className="predhistory__stat-card">
                        <span className="predhistory__stat-label">Total</span>
                        <span className="predhistory__stat-value">{stats.total}</span>
                    </div>
                    <div className="predhistory__stat-card predhistory__stat-card--won">
                        <span className="predhistory__stat-label">Won ✅</span>
                        <span className="predhistory__stat-value">{stats.won}</span>
                    </div>
                    <div className="predhistory__stat-card predhistory__stat-card--lost">
                        <span className="predhistory__stat-label">Lost ❌</span>
                        <span className="predhistory__stat-value">{stats.lost}</span>
                    </div>
                    <div className="predhistory__stat-card predhistory__stat-card--pending">
                        <span className="predhistory__stat-label">Pending ⏳</span>
                        <span className="predhistory__stat-value">{stats.pending}</span>
                    </div>
                    <div className="predhistory__stat-card predhistory__stat-card--rate">
                        <span className="predhistory__stat-label">Win Rate</span>
                        <span className="predhistory__stat-value">
                            {stats.won + stats.lost > 0 ? `${stats.winRate}%` : '—'}
                        </span>
                        {stats.won + stats.lost > 0 && (
                            <div className="predhistory__winrate-bar">
                                <div
                                    className="predhistory__winrate-fill"
                                    style={{ width: `${stats.winRate}%` }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* FILTER TABS */}
                <div className="predhistory__filters">
                    {[
                        { key: 'all', label: 'All Engines' },
                        { key: 'v10', label: 'V10.0' },
                        { key: 'hrhr', label: '🎯 HRHR' }
                    ].map(tab => (
                        <button
                            key={tab.key}
                            className={`predhistory__filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveFilter(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* PREDICTION LIST */}
                <div className="predhistory__list">
                    {isLoading ? (
                        <div className="predhistory__loading">
                            <div className="predhistory__spinner" />
                            <p>Loading predictions...</p>
                        </div>
                    ) : filteredPredictions.length === 0 ? (
                        <div className="predhistory__empty">
                            <Icon icon="solar:clipboard-remove-bold-duotone" className="predhistory__empty-icon" />
                            <h3>No predictions yet</h3>
                            <p>
                                {activeFilter === 'all'
                                    ? 'Your V10 and HRHR predictions will appear here after your next search.'
                                    : `No ${activeFilter === 'v10' ? 'V10.0' : 'HRHR'} predictions found.`
                                }
                            </p>
                            <button
                                className="predhistory__btn predhistory__btn--primary"
                                onClick={() => navigate('/ai-assistant')}
                            >
                                Make a Prediction
                            </button>
                        </div>
                    ) : (
                        <>
                            {filteredPredictions.map((prediction) => {
                                const badge = getEngineBadge(prediction.agent);
                                const resultInfo = getResultInfo(prediction.result);
                                const isExpanded = expandedId === prediction.id;
                                const isUpdating = updatingResult === prediction.id;

                                return (
                                    <div
                                        key={prediction.id}
                                        className={`predhistory__card ${isExpanded ? 'expanded' : ''}`}
                                    >
                                        {/* Card Header - Always Visible */}
                                        <div
                                            className="predhistory__card-header"
                                            onClick={() => setExpandedId(isExpanded ? null : prediction.id)}
                                        >
                                            <div className="predhistory__card-left">
                                                <span
                                                    className="predhistory__engine-badge"
                                                    style={{ color: badge.color, background: badge.bg }}
                                                >
                                                    {badge.label}
                                                </span>
                                                <span className="predhistory__date">
                                                    {formatDate(prediction.createdAt)}
                                                </span>
                                            </div>

                                            <div className="predhistory__card-center">
                                                <p className="predhistory__question-preview">
                                                    {getQuestionPreview(prediction.question)}
                                                </p>
                                            </div>

                                            <div className="predhistory__card-right">
                                                <span
                                                    className="predhistory__result-badge"
                                                    style={{ color: resultInfo.color, background: resultInfo.bg }}
                                                >
                                                    {resultInfo.icon} {resultInfo.label}
                                                </span>
                                                <Icon
                                                    icon="solar:alt-arrow-down-linear"
                                                    className={`predhistory__expand-icon ${isExpanded ? 'open' : ''}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                            <div className="predhistory__card-body">
                                                {/* Matches Queried */}
                                                <div className="predhistory__matches-section">
                                                    <h4 className="predhistory__section-label">Matches Analyzed</h4>
                                                    <pre className="predhistory__matches-text">
                                                        {prediction.question}
                                                    </pre>
                                                </div>

                                                {/* Full Analysis */}
                                                <div className="predhistory__analysis-section">
                                                    <h4 className="predhistory__section-label">Full Analysis</h4>
                                                    <div className="predhistory__analysis-content">
                                                        <ReactMarkdown>{prediction.answer || 'No analysis available.'}</ReactMarkdown>
                                                    </div>
                                                </div>

                                                {/* Result Toggle */}
                                                <div className="predhistory__result-section">
                                                    <h4 className="predhistory__section-label">Mark Result</h4>
                                                    <div className="predhistory__result-buttons">
                                                        {[
                                                            { key: 'won', label: '✅ Won', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)' },
                                                            { key: 'lost', label: '❌ Lost', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
                                                            { key: 'pending', label: '⏳ Pending', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)' }
                                                        ].map(opt => (
                                                            <button
                                                                key={opt.key}
                                                                className={`predhistory__result-btn ${(prediction.result || 'pending') === opt.key ? 'active' : ''}`}
                                                                style={{
                                                                    '--btn-color': opt.color,
                                                                    '--btn-bg': opt.bg
                                                                }}
                                                                onClick={() => updateResult(prediction.id, opt.key)}
                                                                disabled={isUpdating}
                                                            >
                                                                {isUpdating ? '...' : opt.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {prediction.resultUpdatedAt && (
                                                        <p className="predhistory__result-updated">
                                                            Last updated: {formatDate(prediction.resultUpdatedAt)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <div className="predhistory__load-more">
                                    <button
                                        className="predhistory__btn predhistory__btn--secondary"
                                        onClick={() => fetchPredictions(true)}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore ? (
                                            <>
                                                <div className="predhistory__spinner predhistory__spinner--small" />
                                                Loading...
                                            </>
                                        ) : (
                                            'Load More Predictions'
                                        )}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default PredictionHistory;